import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import AccountService from '../../services/AccountService';
import { toast } from './V2Components';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

/**
 * Pending-deletion recovery banner.
 *
 * Rendered when the authenticated user has a pending account deletion.
 * Lets them cancel the deletion in one click before the grace period
 * expires. Sits above the main layout so it's visible on every page.
 *
 * @param {Object} props
 * @param {string} props.scheduledDeletionAt - ISO timestamp when the
 *   account will be permanently deleted.
 * @param {boolean} [props.subscriptionWillBeCanceled] - True when the purge
 *   that completes the deletion will also cancel a Stripe subscription
 *   (`subscription_will_be_canceled` from /account/deletion-status). Billing
 *   keeps running during the grace period, so the banner says it up front.
 * @param {Function} props.onCancelled - Called after a successful cancel
 *   so the parent can clear deletion-pending state and unmount the banner.
 */
const PendingDeletionBanner = ({ scheduledDeletionAt, subscriptionWillBeCanceled = false, onCancelled }) => {
    const [cancelling, setCancelling] = useState(false);

    const formattedDate = (() => {
        if (!scheduledDeletionAt) return 'soon';
        // new Date('bad-string') does NOT throw — it returns an Invalid Date
        // whose toLocaleDateString() returns the literal "Invalid Date".
        // Check the timestamp explicitly so the 'soon' fallback fires for
        // malformed inputs.
        const parsed = new Date(scheduledDeletionAt);
        if (Number.isNaN(parsed.getTime())) return 'soon';
        return parsed.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    })();

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await AccountService.cancelDeletion();
            analytics.track(ANALYTICS_EVENTS.ACCOUNT_DELETE_CANCELLED, {});
            toast.success('Account deletion cancelled.');
            if (onCancelled) onCancelled();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel deletion.';
            toast.error(message);
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div
            role="alert"
            className="w-full bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-900/40 px-4 py-3"
        >
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-red-800 dark:text-red-200">
                            Your account is scheduled for deletion on {formattedDate}.
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                            {subscriptionWillBeCanceled
                                ? 'All your data will be permanently removed and your paid subscription will be canceled. Cancel below to keep your account.'
                                : 'All your data will be permanently removed. Cancel below to keep your account.'}
                        </p>
                    </div>
                </div>
                {/* Destructive-action button uses the canonical bg-red-500/red-600
                    palette from ConfirmationModal (V2Components.jsx) so all
                    "confirm-the-bad-thing" buttons share one visual identity. */}
                <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition disabled:opacity-50"
                >
                    <X size={16} />
                    {cancelling ? 'Cancelling...' : 'Cancel deletion'}
                </button>
            </div>
        </div>
    );
};

export default PendingDeletionBanner;
